import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
} from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AuctionCatalogsService } from '@viewbid/ts-angular-client';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { getCategoryClass } from '@/vb-utils/auction.util';

@Component({
    selector: 'vb-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.scss'],
})
export class SearchComponent implements OnInit, OnDestroy {
    @Input() placeholder: string = 'Search for lots, categories…';
    @Input() ariaLabel: string = 'Search for lots and categories';
    /** When true, this component doesn't call searchAny or render its own
     *  results dropdown — it only emits the debounced search text via
     *  `search` and lets the parent decide what to fetch with it. */
    @Input() delegateSearch: boolean = false;
    @Output() search: EventEmitter<string> = new EventEmitter<string>();
    public searchText: string = '';
    isDropdownOpen: boolean = false;
    public searchList: any = {};
    public noResults: boolean = false;
    private searchSubject: Subject<string> = new Subject();
    public thumbnail: string = '';
    public lotList: any = [];
    public auctionsList: any = [];
    public isLoading: boolean = false;
    private clickListener: (() => void) | undefined;
    private readonly destroy$: Subject<void> = new Subject<void>();

    constructor(
        private searchService: AuctionCatalogsService,
        private renderer: Renderer2,
        private elRef: ElementRef,
        private sanitizer: DomSanitizer,
        private router: Router,
    ) {}

    ngOnInit(): void {
        this.searchSubject
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                takeUntil(this.destroy$),
            )
            .subscribe((searchText: string) => {
                if (this.delegateSearch) {
                    this.search.emit(searchText.trim());
                    return;
                }
                if (!searchText || searchText.trim().length < 3) {
                    this.isLoading = false;
                    this.noResults = false;
                    this.searchList = [];
                    return;
                }
                this.isLoading = true;
                this.noResults = false;
                this.searchService.searchAny(searchText, 1, 10).subscribe({
                    next: (result: any) => {
                        this.isLoading = false;
                        this.noResults = result.data.length === 0;
                        this.searchList = result.data;
                        this.lotList = this.searchList.filter(
                            (element: any) => element.type === 'Lots',
                        );
                        this.auctionsList = this.searchList.filter(
                            (element: any) => element.type === 'Auctions',
                        );
                    },
                    error: () => {
                        this.isLoading = false;
                        this.noResults = true;
                    },
                });
            });
        this.clickListener = this.renderer.listen(
            'document',
            'mousedown',
            (e: Event) => {
                const target: HTMLElement | null = e.target as HTMLElement;
                // Only close if we have a valid target and it's outside our component
                if (target && !this.elRef.nativeElement.contains(target)) {
                    this.isDropdownOpen = false;
                    this.isLoading = false;
                }
            },
        );
    }

    handleSearchSubmit(event: Event): void {
        const input: HTMLInputElement = event.target as HTMLInputElement;
        this.searchText = input.value;
        this.searchSubject.next(input.value);
        this.isDropdownOpen = true;
    }

    clearSearch(input: HTMLInputElement): void {
        this.searchText = '';
        input.value = '';
        input.focus();
        // Route the empty value through the subject so re-typing the same
        // query isn't swallowed by distinctUntilChanged.
        this.searchSubject.next('');
        this.isDropdownOpen = false;
        this.isLoading = false;
        this.noResults = false;
        this.searchList = [];
        this.lotList = [];
        this.auctionsList = [];
    }

    sanitizeDescription(description: string): SafeHtml {
        if (!description) return '';

        // Extract content from first HTML tag if it exists; the row's CSS
        // ellipsis handles length.
        const firstTagMatch: RegExpExecArray | null =
            /<[^>]*>([^<]*)<\/[^>]*>/.exec(description);
        const content: string = firstTagMatch ? firstTagMatch[1] : description;

        return this.sanitizer.bypassSecurityTrustHtml(content);
    }

    navigate(auctionId: string, lotId?: string): void {
        this.isDropdownOpen = false;
        const url: string = lotId
            ? `/auctions/${auctionId}/lot/${lotId}`
            : `/auctions/${auctionId}`;
        this.router
            .navigateByUrl('/', { skipLocationChange: true })
            .then(() => this.router.navigateByUrl(url));
    }

    /**
     * Checks the given category against the keys from categoryToClassMap.
     * Returns the corresponding CSS class if a match is found, otherwise returns the default class for 'restaurant'.
     *
     * @param category The category to check.
     * @returns The corresponding CSS class or the default class if no matching category is found.
     */
    getCategoryClass(category: string): string {
        return getCategoryClass(category);
    }

    ngOnDestroy(): void {
        if (this.clickListener) {
            this.clickListener();
        }
        this.destroy$.next();
        this.destroy$.complete();
    }
}
